import { Box, CircularProgress, Typography, Button, Stack } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import PolicyOutlined from '@mui/icons-material/PolicyOutlined';
import { useParams, useNavigate } from 'react-router';
import { format, parseISO, addDays } from 'date-fns';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import RolesWidget from './roles/roles.widget';
import ProfileWidget from './profile/profile.widget';
import DashboardWidget from '../common/dashboard-widget';
import DashboardWidgetsGrid from '../common/dashboard-widgets-grid';
import { useContributions } from './use-contributions';
import { useContributorSummaryQuery } from '@/state/queries/contributors/user-contributors';
import TopBar from '../common/top-bar';
import { useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import { useGetEmailOptOutsQuery } from '@/state/queries/email-preferences/use-get-email-opt-outs';
import UnblockOrgModal from './unblock-org.modal';
import AcceptedTermsModal from './accepted-terms.modal';
import { useGetUserQuery } from '@/state/queries/users/use-get-user';

const buildEventLink = (event?: EventSummaryDto) => {
  if (!event?.id) return null;

  let navigateToUrl = `/event/${event.id}`;
  if (!event.multiFlow && event?.flows) {
    const singleFlow = event.flows[0];
    if (singleFlow) navigateToUrl = `/flow/${singleFlow.id}`;
  }

  return navigateToUrl;
};

const hasEventManagerOrOwnerRole = (contributor: ContributorSummaryDto | undefined): boolean => {
  if (!contributor?.roles || contributor.roles.length === 0) {
    return false;
  }

  return contributor.roles.some((role) => {
    if (role.entityType !== 'Event') {
      return false;
    }

    if (role.role !== 'event_manager' && role.role !== 'owner') {
      return false;
    }

    return !!role.acceptedAt;
  });
};

export default function ContributionDetailsPage() {
  const { contributorId } = useParams<{ contributorId: string }>();
  // Use lightweight list query to check if there are multiple contributions (for back button)
  const { allContributions } = useContributions({ useListMode: true });
  // Use single contributor query for full data
  const { data: contributor, isLoading } = useContributorSummaryQuery(contributorId);
  const { data: currentUser } = useGetUserQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const t = useTranslations();
  const navigate = useNavigate();

  const event = contributor?.event;
  const { data: optOuts } = useGetEmailOptOutsQuery();
  const [unblockModalDismissed, setUnblockModalDismissed] = useState(false);

  const orgId = event?.ownedBy?.id;
  const matchingOptOuts = optOuts?.filter((o) => orgId && o.organization._id === orgId && !o.silencedAt) ?? [];

  const showUnblockModal = matchingOptOuts.length > 0 && !unblockModalDismissed;

  // Let React Compiler handle memoization instead of manual useMemo
  const canOpenEvent = hasEventManagerOrOwnerRole(contributor);
  const hasAcceptedTerms = (contributor?.acceptedTerms?.length ?? 0) > 0;

  const handleOpenEvent = () => {
    const url = buildEventLink(event);
    if (url) navigate(url);
  };

  const formatDateRange = () => {
    if (!event?.startDate) return t('myEventsList.unknown');
    const start = format(parseISO(event.startDate), 'dd.MM.yy');
    const end = event.durationDays
      ? format(addDays(parseISO(event.startDate), event.durationDays - 1), 'dd.MM.yy')
      : start;
    return start === end ? start : `${start} - ${end}`;
  };

  const resolveInvitedEmail = () => {
    const fromContributor = contributor?.invitedEmail?.trim() || contributor?.email?.trim();
    const fromUserContributor = contributor?.userContributor?.invitedEmail?.trim();
    const fromProfileContact = contributor?.userContributor?.providedProfile?.contactEmail?.trim();
    const fromAccount = currentUser?.primaryEmail?.trim();
    return fromContributor || fromUserContributor || fromProfileContact || fromAccount || undefined;
  };

  const renderEventSubtitle = () => {
    const datePart = formatDateRange();
    const invitedEmail = resolveInvitedEmail();
    if (!invitedEmail) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
          {datePart}
        </Typography>
      );
    }
    return (
      <Stack direction="row" flexWrap="wrap" columnGap={0.75} rowGap={0.25} alignItems="baseline">
        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: 'inherit' }}>
          {datePart}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: 'inherit' }}>
          |
        </Typography>
        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: 'inherit' }}>
          {t('myEventsList.contributor.invitedEmail', { email: invitedEmail })}
        </Typography>
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contributor) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h6" color="text.secondary">
          Contributor not found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {matchingOptOuts.length > 0 && (
        <UnblockOrgModal
          open={showUnblockModal}
          onClose={() => setUnblockModalDismissed(true)}
          optOuts={matchingOptOuts}
        />
      )}
      <OrganizationContentStack
        content={
          <DashboardWidget
            sx={{
              height: '100%',
              p: 0,
              gap: 0,
              width: '100%',
            }}
            contentSx={{ p: 0 }}
          >
            <TopBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              title={event?.title || t('myEventsList.unknown')}
              subtitle={renderEventSubtitle()}
              showSearch={false}
              showBackButton={allContributions.length > 1}
              action={
                hasAcceptedTerms || canOpenEvent ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {hasAcceptedTerms ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<PolicyOutlined />}
                        onClick={() => setTermsModalOpen(true)}
                        size="small"
                        aria-label={t('user.contributionsList.terms')}
                        sx={{
                          'minWidth': { xs: 32, md: 'auto' },
                          'px': { xs: 0.75, md: 1.25 },
                          '& .MuiButton-startIcon': {
                            ml: { xs: 0, md: -0.5 },
                            mr: { xs: 0, md: 1 },
                          },
                        }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                          {t('user.contributionsList.terms')}
                        </Box>
                      </Button>
                    ) : null}
                    {canOpenEvent ? (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<LaunchIcon />}
                        onClick={handleOpenEvent}
                        size="small"
                        aria-label={t('myEventsList.manageEvent')}
                        sx={{
                          'minWidth': { xs: 32, md: 'auto' },
                          'px': { xs: 0.75, md: 1.25 },
                          '& .MuiButton-startIcon': {
                            ml: { xs: 0, md: -0.5 },
                            mr: { xs: 0, md: 1 },
                          },
                        }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                          {t('myEventsList.manageEvent')}
                        </Box>
                      </Button>
                    ) : null}
                  </Stack>
                ) : undefined
              }
            />
            <DashboardWidgetsGrid wideScreenHeightScale={1.5} sx={{ flex: 1, minHeight: 0, p: 2 }}>
              <RolesWidget
                contributor={contributor}
                event={event}
                flows={contributor.flows ?? []}
                sessions={contributor.sessions ?? []}
              />
              <ProfileWidget contributor={contributor} />
            </DashboardWidgetsGrid>
          </DashboardWidget>
        }
      />
      <AcceptedTermsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        acceptedTerms={contributor.acceptedTerms}
      />
    </>
  );
}
